<script setup>
// Lock screen over the whole app: Face ID right away, PIN keypad below it (docs/pwa/DESIGN.md §1).
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { locale, t } from '../i18n';
import {
  assertPasskey, biometricWords, isCancelled, isWebAuthnSupported, storedPasskeyId,
} from '../webauthn';
import PinPad from './PinPad.vue';

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
  lock: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['unlocked', 'signed-out']);

const busy = ref(false);
const status = ref('');
const statusTone = ref('error');
const errorKey = ref(0);
const retryPasskey = ref(false);

const method = computed(() => biometricWords(locale.value));
const supported = isWebAuthnSupported();
// Face ID is called by itself only where this device holds a key, otherwise a PC would show a QR dialog every time.
const deviceHasKey = supported && storedPasskeyId() !== null;
const canUsePasskey = computed(() => supported && props.lock.has_passkey);
const keypadBiometric = computed(() => {
  if (!canUsePasskey.value) {
    return null;
  }
  return deviceHasKey ? 'faceid' : 'key';
});

const displayName = computed(() => props.user.name?.trim() || props.user.email);
const title = computed(() => (props.lock.has_pin ? t('lock.enterPin') : t('lock.locked')));

function setStatus(text, tone = 'error') {
  status.value = text;
  statusTone.value = tone;
}

// Axios got no answer; WebAuthn errors are DOMExceptions and never look like this.
function isNetworkError(e) {
  return Boolean(e?.isAxiosError) && !e.response;
}

async function submitPin(pin) {
  busy.value = true;
  retryPasskey.value = false;
  setStatus(t('lock.checking'), 'muted');
  try {
    const { data } = await axios.post('/app-lock/unlock', { pin });
    status.value = '';
    emit('unlocked', data.lock);
  } catch (e) {
    const response = e?.response;
    if (response?.status === 401) {
      emit('signed-out', response.data?.reason === 'pin_attempts' ? 'pin_attempts' : 'session');
      return;
    }
    errorKey.value += 1;
    if (isNetworkError(e)) {
      setStatus(t('lock.network'));
    } else if (response?.status === 422 && Number.isInteger(response.data?.attempts_left)) {
      const left = response.data.attempts_left;
      if (left === 1) {
        setStatus(t('lock.wrongPinLast'), 'warning');
      } else {
        setStatus(t('lock.wrongPin', { n: left }));
      }
    } else {
      setStatus(t('lock.failed'));
    }
  } finally {
    busy.value = false;
  }
}

async function usePasskey() {
  if (!canUsePasskey.value || busy.value) {
    return;
  }
  busy.value = true;
  retryPasskey.value = false;
  status.value = '';
  try {
    const data = await assertPasskey('/app-lock/passkey/options', '/app-lock/passkey');
    emit('unlocked', data.lock);
  } catch (e) {
    if (e?.response?.status === 401) {
      emit('signed-out', 'session');
      return;
    }
    if (isCancelled(e)) {
      return;
    }
    if (isNetworkError(e)) {
      retryPasskey.value = true;
      setStatus(t('lock.network'));
      return;
    }
    setStatus(props.lock.has_pin
      ? t('lock.passkeyFailedPin', method.value)
      : t('lock.passkeyFailedRetry', method.value));
  } finally {
    busy.value = false;
  }
}

async function signOut() {
  busy.value = true;
  try {
    await axios.post('/auth/logout');
  } catch {
    // Already signed out or offline: the login form is shown either way.
  } finally {
    busy.value = false;
    emit('signed-out', 'manual');
  }
}

onMounted(() => {
  if (canUsePasskey.value && deviceHasKey) {
    usePasskey();
  }
});
</script>

<template>
  <div
    class="fixed inset-0 z-[200] flex h-dvh flex-col items-center overflow-y-auto bg-slate-950 px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-[max(env(safe-area-inset-top),2rem)] text-slate-100"
    role="dialog"
    aria-modal="true"
    aria-labelledby="lock-title"
  >
    <div class="flex w-full max-w-xs flex-1 flex-col items-center justify-center">
      <span
        class="flex size-12 items-center justify-center rounded-xl bg-slate-900 text-base font-bold tracking-tight text-emerald-400 ring-1 ring-slate-700/80"
        aria-hidden="true"
      >DT</span>
      <p class="mt-3 max-w-full truncate text-base text-slate-300">
        {{ displayName }}
      </p>

      <h2
        id="lock-title"
        class="mt-8 text-lg font-semibold"
      >
        {{ title }}
      </h2>

      <PinPad
        v-if="lock.has_pin"
        class="mt-6"
        :disabled="busy"
        :error-key="errorKey"
        :biometric="keypadBiometric"
        :biometric-words="method"
        @complete="submitPin"
        @biometric="usePasskey"
      />

      <p
        class="mt-4 flex min-h-10 flex-col items-center text-center text-sm"
        :class="{
          'text-rose-400': statusTone === 'error',
          'text-amber-400': statusTone === 'warning',
          'text-slate-400': statusTone === 'muted',
        }"
        role="alert"
      >
        <span>{{ status }}</span>
        <button
          v-if="retryPasskey"
          type="button"
          class="mt-1 text-slate-300 underline underline-offset-2"
          @click="usePasskey"
        >
          {{ t('lock.retry') }}
        </button>
      </p>

      <button
        v-if="!lock.has_pin && canUsePasskey"
        type="button"
        class="mt-2 h-12 w-full max-w-xs rounded-xl bg-emerald-600 font-medium transition hover:bg-emerald-500 disabled:opacity-60"
        :disabled="busy"
        @click="usePasskey"
      >
        {{ t('lock.signInWith', method) }}
      </button>
    </div>

    <button
      type="button"
      class="mt-6 py-2 text-sm text-slate-400 transition hover:text-slate-200"
      :disabled="busy"
      @click="signOut"
    >
      {{ t('app.logout') }}
    </button>
  </div>
</template>

